import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import Header from "@/components/Header";
import { formatPrice } from "@/components/ProductCard";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Trash, Minus, Plus, ShoppingCart, Tag, ArrowLeft, Lock, CreditCard, Bank, MapPin } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const PLACEHOLDER = "https://images.unsplash.com/photo-1495100497150-fe209c585f50?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";

export default function Cart() {
  const navigate = useNavigate();
  const { items, remove, setQty, changeVariant, clear } = useCart();
  const { user, login, setUser } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [coupon, setCoupon] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState("");
  const [paying, setPaying] = useState(false);
  const [methods, setMethods] = useState(null);
  const [paymentType, setPaymentType] = useState("mercadopago");
  const [addressId, setAddressId] = useState("");
  const [showAddrForm, setShowAddrForm] = useState(false);
  const [savingAddr, setSavingAddr] = useState(false);
  const [newAddr, setNewAddr] = useState({ label: "", recipient: "", phone: "", street: "", number: "", apartment: "", city: "", state: "", zip: "" });

  useEffect(() => {
    api.get("/settings").then((r) => {
      const pm = r.data.payment_methods || {};
      setMethods(pm);
      if (!pm.mercadopago?.enabled && pm.transfer?.enabled) setPaymentType("transfer");
    });
  }, []);

  useEffect(() => {
    const def = (user?.addresses || []).find((a) => a.is_default) || (user?.addresses || [])[0];
    if (def) setAddressId(def.id);
  }, [user]);

  const fetchSummary = useCallback(async () => {
    if (items.length === 0) { setSummary(null); setLoading(false); return; }
    setLoading(true);
    try {
      const res = await api.post("/cart/summary", { items, coupon_code: appliedCoupon || null });
      setSummary(res.data);
      if (res.data.coupon_error) toast.error(res.data.coupon_error);
    } finally { setLoading(false); }
  }, [items, appliedCoupon]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const applyCoupon = () => { setAppliedCoupon(coupon.trim().toUpperCase()); };

  const saveNewAddress = async () => {
    if (!newAddr.street || !newAddr.city) { toast.error("Completa al menos calle y ciudad"); return; }
    setSavingAddr(true);
    try {
      const id = (crypto.randomUUID && crypto.randomUUID()) || String(Date.now());
      const existing = user.addresses || [];
      const addr = { ...newAddr, id, is_default: existing.length === 0 };
      const res = await api.put("/auth/profile", {
        name: user.name, phone: user.phone || null,
        addresses: [...existing, addr], billing: user.billing || null,
      });
      setUser(res.data);
      setAddressId(id);
      setShowAddrForm(false);
      setNewAddr({ label: "", recipient: "", phone: "", street: "", number: "", apartment: "", city: "", state: "", zip: "" });
      toast.success("Dirección agregada");
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo guardar la dirección"); }
    finally { setSavingAddr(false); }
  };

  const pay = async () => {
    if (!user) { toast("Inicia sesión para pagar"); login(); return; }
    if (!addressId) { toast.error("Selecciona o agrega una dirección de envío"); setShowAddrForm((user.addresses || []).length === 0); return; }
    setPaying(true);
    try {
      const address = (user.addresses || []).find((a) => a.id === addressId) || null;
      const res = await api.post("/checkout/cart", {
        items, coupon_code: appliedCoupon || null,
        payment_type: paymentType, shipping_address: address, billing: user.billing || null,
      });
      navigate(`/checkout/${res.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo continuar");
    } finally { setPaying(false); }
  };

  if (items.length === 0) {
    return (
      <div className="App min-h-screen bg-background">
        <Header />
        <div className="max-w-md mx-auto px-4 py-20 text-center">
          <div className="bg-white rounded-lg nb-shadow p-8" data-testid="empty-cart">
            <ShoppingCart weight="bold" size={40} className="mx-auto text-primary" />
            <h1 className="text-2xl font-extrabold tracking-tight mt-4">Tu carrito está vacío</h1>
            <p className="text-muted-foreground mt-2">Agrega productos para comenzar tu compra.</p>
            <Link to="/" className="mt-6 inline-block nb-btn bg-primary text-primary-foreground px-6 py-3 hover:brightness-110">Ir a comprar</Link>
          </div>
        </div>
      </div>
    );
  }

  const lines = summary?.items || [];

  return (
    <div className="App min-h-screen bg-background">
      <Header />
      <div className="max-w-[1200px] mx-auto px-3 sm:px-5 py-5">
        <Link to="/" className="text-[13px] text-primary flex items-center gap-1 hover:underline mb-3"><ArrowLeft weight="bold" size={14} /> Continuar comprando</Link>
        <h1 className="text-2xl font-extrabold tracking-tight mb-4">Carrito de compras</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Items */}
          <div className="lg:col-span-2 bg-white rounded-lg nb-shadow divide-y divide-black/5" data-testid="cart-items">
            {loading && !summary ? (
              <div className="p-8 text-center text-muted-foreground">Cargando…</div>
            ) : lines.map((l) => {
              const key = `${l.product_id}|${l.variant_id || ""}`;
              return (
                <div key={key} className="p-4 flex gap-4" data-testid={`cart-item-${l.product_id}`}>
                  <Link to={`/producto/${l.product_id}`} className="w-20 h-20 rounded-md overflow-hidden bg-secondary shrink-0">
                    <img src={l.product_image ? fileUrl(l.product_image) : PLACEHOLDER} alt="" className="w-full h-full object-cover" onError={(e)=>{e.currentTarget.src=PLACEHOLDER;}} />
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Link to={`/producto/${l.product_id}`} className="font-medium text-sm hover:text-primary line-clamp-2">{l.product_title}</Link>
                    {l.variants && l.variants.length > 0 ? (
                      <div className="mt-1">
                        <Select value={l.variant_id || ""} onValueChange={(v) => changeVariant(l.product_id, l.variant_id, v)}>
                          <SelectTrigger data-testid={`cart-variant-${l.product_id}`} className="h-8 w-auto text-xs rounded-md"><SelectValue /></SelectTrigger>
                          <SelectContent className="rounded-md">
                            {l.variants.map((v) => <SelectItem key={v.id} value={v.id} disabled={v.stock < 1}>{v.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    ) : null}
                    <p className="text-xs text-muted-foreground mt-1">Precio unitario: {formatPrice(l.unit_price)}</p>
                    {l.available_stock < l.quantity && <p className="text-xs text-destructive mt-0.5">Solo {l.available_stock} disponibles</p>}
                    <div className="flex items-center gap-3 mt-2">
                      <div className="flex items-center border border-black/15 rounded-md">
                        <button onClick={() => setQty(l.product_id, l.variant_id, l.quantity - 1)} data-testid={`qty-minus-${l.product_id}`} className="px-2 py-1 hover:bg-secondary"><Minus weight="bold" size={13} /></button>
                        <span className="px-3 text-sm font-semibold" data-testid={`qty-${l.product_id}`}>{l.quantity}</span>
                        <button onClick={() => setQty(l.product_id, l.variant_id, l.quantity + 1)} data-testid={`qty-plus-${l.product_id}`} className="px-2 py-1 hover:bg-secondary"><Plus weight="bold" size={13} /></button>
                      </div>
                      <button onClick={() => remove(l.product_id, l.variant_id)} data-testid={`remove-${l.product_id}`} className="text-xs text-primary hover:underline flex items-center gap-1"><Trash weight="bold" size={14} /> Eliminar</button>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-bold">{formatPrice(l.subtotal)}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Summary */}
          <div className="bg-white rounded-lg nb-shadow p-5 h-fit lg:sticky lg:top-24" data-testid="cart-summary">
            <h2 className="font-extrabold tracking-tight text-lg mb-3">Resumen de compra</h2>
            {/* Coupon */}
            <div className="mb-4">
              <label className="text-xs font-bold uppercase tracking-wide flex items-center gap-1"><Tag weight="bold" size={13} /> Cupón</label>
              <div className="flex gap-2 mt-1">
                <input value={coupon} onChange={(e) => setCoupon(e.target.value)} placeholder="Código" data-testid="coupon-input"
                       className="flex-1 border border-black/15 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
                <button onClick={applyCoupon} data-testid="apply-coupon-button" className="nb-btn bg-secondary px-3 py-2 text-sm hover:bg-primary hover:text-white">Aplicar</button>
              </div>
              {summary?.coupon && <p className="text-xs text-[hsl(var(--meli-green))] mt-1 font-semibold">Cupón {summary.coupon.code} aplicado</p>}
              {summary?.coupon_error && <p className="text-xs text-destructive mt-1">{summary.coupon_error}</p>}
            </div>

            <div className="space-y-2 text-sm border-t border-black/10 pt-3">
              <div className="flex justify-between"><span>Subtotal</span><span data-testid="summary-subtotal">{formatPrice(summary?.subtotal)}</span></div>
              {summary?.discount > 0 && <div className="flex justify-between text-[hsl(var(--meli-green))]"><span>Descuento</span><span data-testid="summary-discount">-{formatPrice(summary.discount)}</span></div>}
              <div className="flex justify-between"><span>Envío</span><span data-testid="summary-shipping">{summary?.shipping === 0 ? "Gratis" : formatPrice(summary?.shipping)}</span></div>
              {summary?.credit_applied > 0 && <div className="flex justify-between text-emerald-600"><span>Saldo a favor</span><span data-testid="summary-credit">-{formatPrice(summary.credit_applied)}</span></div>}
              <div className="flex justify-between text-lg font-extrabold border-t border-black/10 pt-2 mt-1"><span>Total</span><span data-testid="summary-total">{formatPrice(summary?.total)}</span></div>
            </div>

            {lines.some((l) => l.is_adult) && (
              <div className="mt-4 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="cart-adult-warning">
                Tu carrito incluye productos para adultos (+18). <b>Estos productos no tienen garantía.</b>
              </div>
            )}

            {user && (
              <div className="mt-4" data-testid="address-section">
                <label className="text-xs font-bold uppercase tracking-wide flex items-center gap-1"><MapPin weight="bold" size={13} /> Dirección de envío</label>
                {(user.addresses || []).length > 0 && !showAddrForm && (
                  <>
                    <Select value={addressId} onValueChange={setAddressId}>
                      <SelectTrigger data-testid="address-select" className="rounded-md text-sm mt-1"><SelectValue placeholder="Elegir dirección" /></SelectTrigger>
                      <SelectContent className="rounded-md">
                        {user.addresses.map((a) => <SelectItem key={a.id} value={a.id}>{a.label || "Dirección"} — {[a.street, a.number, a.city].filter(Boolean).join(" ")}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <button type="button" onClick={() => setShowAddrForm(true)} data-testid="add-another-address" className="text-xs text-primary hover:underline mt-1.5 flex items-center gap-1"><Plus weight="bold" size={12} /> Agregar otra dirección</button>
                  </>
                )}

                {((user.addresses || []).length === 0 || showAddrForm) && (
                  <div className="mt-2 border border-black/10 rounded-lg p-3 space-y-2" data-testid="new-address-form">
                    <div className="grid grid-cols-2 gap-2">
                      <input value={newAddr.label} onChange={(e) => setNewAddr({ ...newAddr, label: e.target.value })} placeholder="Etiqueta (Casa)" data-testid="addr-label" className="border border-black/15 rounded-md px-2 py-1.5 text-sm col-span-2" />
                      <input value={newAddr.recipient} onChange={(e) => setNewAddr({ ...newAddr, recipient: e.target.value })} placeholder="Destinatario" data-testid="addr-recipient" className="border border-black/15 rounded-md px-2 py-1.5 text-sm" />
                      <input value={newAddr.phone} onChange={(e) => setNewAddr({ ...newAddr, phone: e.target.value })} placeholder="Teléfono" data-testid="addr-phone" className="border border-black/15 rounded-md px-2 py-1.5 text-sm" />
                      <input value={newAddr.street} onChange={(e) => setNewAddr({ ...newAddr, street: e.target.value })} placeholder="Calle" data-testid="addr-street" className="border border-black/15 rounded-md px-2 py-1.5 text-sm" />
                      <input value={newAddr.number} onChange={(e) => setNewAddr({ ...newAddr, number: e.target.value })} placeholder="Número" data-testid="addr-number" className="border border-black/15 rounded-md px-2 py-1.5 text-sm" />
                      <input value={newAddr.apartment} onChange={(e) => setNewAddr({ ...newAddr, apartment: e.target.value })} placeholder="Piso/Depto" data-testid="addr-apartment" className="border border-black/15 rounded-md px-2 py-1.5 text-sm" />
                      <input value={newAddr.zip} onChange={(e) => setNewAddr({ ...newAddr, zip: e.target.value })} placeholder="Código postal" data-testid="addr-zip" className="border border-black/15 rounded-md px-2 py-1.5 text-sm" />
                      <input value={newAddr.city} onChange={(e) => setNewAddr({ ...newAddr, city: e.target.value })} placeholder="Ciudad" data-testid="addr-city" className="border border-black/15 rounded-md px-2 py-1.5 text-sm" />
                      <input value={newAddr.state} onChange={(e) => setNewAddr({ ...newAddr, state: e.target.value })} placeholder="Provincia" data-testid="addr-state" className="border border-black/15 rounded-md px-2 py-1.5 text-sm" />
                    </div>
                    <div className="flex gap-2">
                      <button type="button" onClick={saveNewAddress} disabled={savingAddr} data-testid="save-address-button" className="nb-btn bg-primary text-primary-foreground px-3 py-1.5 text-xs hover:brightness-110 disabled:opacity-50">{savingAddr ? "Guardando…" : "Guardar dirección"}</button>
                      {(user.addresses || []).length > 0 && <button type="button" onClick={() => setShowAddrForm(false)} className="text-xs text-muted-foreground hover:text-foreground px-2">Cancelar</button>}
                    </div>
                  </div>
                )}
              </div>
            )}

            {methods && (
              <div className="mt-4">
                <label className="text-xs font-bold uppercase tracking-wide">Forma de pago</label>
                <div className="mt-1 space-y-2" data-testid="payment-methods">
                  {methods.mercadopago?.enabled && (
                    <label className={`flex items-center gap-2 border rounded-md px-3 py-2 text-sm cursor-pointer ${paymentType === "mercadopago" ? "border-primary ring-1 ring-primary/30" : "border-black/10"}`}>
                      <input type="radio" name="pm" checked={paymentType === "mercadopago"} onChange={() => setPaymentType("mercadopago")} data-testid="pm-mercadopago" />
                      <CreditCard weight="bold" size={16} className="text-[#009EE3]" /> Tarjeta de crédito o débito
                    </label>
                  )}
                  {methods.transfer?.enabled && (
                    <label className={`flex items-center gap-2 border rounded-md px-3 py-2 text-sm cursor-pointer ${paymentType === "transfer" ? "border-primary ring-1 ring-primary/30" : "border-black/10"}`}>
                      <input type="radio" name="pm" checked={paymentType === "transfer"} onChange={() => setPaymentType("transfer")} data-testid="pm-transfer" />
                      <Bank weight="bold" size={16} className="text-[#2D3277]" /> Transferencia bancaria
                    </label>
                  )}
                </div>
                {paymentType === "transfer" && <p className="text-[11px] text-muted-foreground mt-1.5">Verás los datos bancarios y subirás el comprobante en el siguiente paso.</p>}
              </div>
            )}

            <button onClick={pay} disabled={paying || (methods && !methods.mercadopago?.enabled && !methods.transfer?.enabled)} data-testid="checkout-button"
                    className="w-full nb-btn bg-primary text-primary-foreground py-3 mt-4 hover:brightness-110 disabled:opacity-50">
              {paying ? "Procesando…" : "Continuar compra"}
            </button>
            <p className="text-[11px] text-muted-foreground mt-2 flex items-center justify-center gap-1"><Lock weight="bold" size={12} /> Compra protegida</p>
            <button onClick={clear} className="w-full text-xs text-muted-foreground hover:text-destructive mt-3">Vaciar carrito</button>
          </div>
        </div>
      </div>
    </div>
  );
}
